import React, { forwardRef, useEffect, useImperativeHandle } from "react";
import { useSelector } from "react-redux";
import {
  Col,
  Row,
  textVr,
  UiBox,
  UiCenter,
  UiFormControl,
  UiFormErrorMessage,
  UiFormLabel,
  UiGrid,
  UiGridItem,
  UiHStack,
  UiInput,
  UiInputProps,
  UiPrimaryButton,
  UiRadio,
  UiRadioGroup,
  UiText,
  useBreakpointValue,
} from "~/components/elements";
import { GENDER } from "~/constants/dashboard";
import { useAppDispatch, useInput } from "~/hooks";
import {
  ConfirmKeyFeature,
  EDashboardStep,
  setActiveStep,
  setConfirmKeyFeature,
  setConfirmKeyFeature2,
  setDashboardProgress,
} from "~/redux/dashboards/slice";
import { RootState } from "~/redux/root-reducer";
// eslint-disable-next-line no-restricted-imports
import { StyleProps } from "@chakra-ui/react";
import { PageLayout } from "~/components/layouts/PageLayout";
import { DashboardMainBox } from "~/components/shared/DashboardMainBox";
import { DashboardStepper } from "~/components/shared/DashboardStepper";
import { useHistory } from "react-router-dom";
import { EthnicityDropdown, EyeColorDropdown, HairColorDropdown, HairLengthDropdown } from "~/components/dropdowns";
import { PAGES } from "~/constants";
import isEmpty from "lodash/isEmpty";
import { PhotoshootType } from "~/types/models/dashboard";

interface KeyFeaturesInputProps extends StyleProps, UiInputProps {
  label: string;
  isInvalid: boolean;
  errorMsg?: string;
}

function KeyFeaturesInputField({ label, isInvalid, errorMsg, ...rest }: KeyFeaturesInputProps) {
  return (
    <UiFormControl isRequired isInvalid={isInvalid}>
      <UiFormLabel {...textVr("body2")} color="neutral.gray800">
        {label}
      </UiFormLabel>
      <UiInput w="100%" type="text" boxShadow="none" _invalid={{ boxShadow: "none", borderColor: "red" }} {...rest} />
      {errorMsg && (
        <UiFormErrorMessage mt={1} fontSize="12px" color="red">
          {errorMsg}
        </UiFormErrorMessage>
      )}
    </UiFormControl>
  );
}

// NEW: Add ref interface for PersonForm
interface PersonFormRef {
  validate: () => boolean;
  getData: () => ConfirmKeyFeature;
}

interface PersonFormProps {
  initialData?: ConfirmKeyFeature | null;
  personNo: 1 | 2;
  showRelationshipField: boolean;
}

// NEW: Use forwardRef to expose validation method
const PersonForm = forwardRef<PersonFormRef, PersonFormProps>(
  ({ initialData, personNo, showRelationshipField }, ref) => {
    const rules = {
      name: { presence: true },
      age: { presence: true },
      ethnicity: { presence: true },
      eye_color: { presence: true },
      hair_color: { presence: true },
      hair_length: { presence: true },
      sex: { presence: true },
      // NEW: required field relationship for person 2
      ...(showRelationshipField && { relationship: { presence: true } }),
    };

    const [inputs, setInputs, { validation }] = useInput<ConfirmKeyFeature>(initialData || {}, { rules });

    // NEW: State to control when to show validation errors
    const [shouldShowErrors, setShouldShowErrors] = React.useState(false);

    useEffect(() => {
      if (!isEmpty(initialData)) {
        setInputs(initialData);
      }
    }, [initialData, setInputs]);

    // NEW: Expose validation method to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        validate: () => {
          setShouldShowErrors(true);
          return !!validation.getErrors();
        },
        getData: () => ({
          age: +inputs.age!,
          name: inputs.name?.trim(),
          ethnicity: inputs.ethnicity?.trim(),
          eye_color: inputs.eye_color,
          hair_color: inputs.hair_color,
          hair_length: inputs.hair_length,
          sex: inputs.sex,
          personNo,
          ...(showRelationshipField && { relationship: inputs.relationship?.trim() }),
        }),
      }),
      [inputs, personNo, showRelationshipField, validation]
    );

    const onSelectGender = (value: string) => {
      setInputs({ sex: value });
    };

    // NEW: Only show validation errors when shouldShowErrors is true
    const getValidationError = (field: string) => (shouldShowErrors ? validation?.errors?.[field]?.[0] : undefined);

    const isFieldInvalid = (field: string) => shouldShowErrors && !!validation?.errors?.[field];

    return (
      <UiGrid
        rounded="3xl"
        bg="neutral.gray00"
        templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(6, 1fr)" }}
        rowGap={{ base: 4, md: 5 }}
        columnGap={3}
        mt={{ base: 5, md: 8 }}
        p={{ base: 4, md: 5 }}
        w="full"
      >
        <UiGridItem colSpan={{ base: 1, md: 6 }}>
          <KeyFeaturesInputField
            isInvalid={isFieldInvalid("name")}
            errorMsg={getValidationError("name")}
            label="Full Name"
            placeholder="Enter your name"
            value={inputs.name}
            onChange={({ target }) => setInputs({ name: target.value })}
          />
        </UiGridItem>
        <UiGridItem colSpan={{ base: 1, md: 1 }}>
          <KeyFeaturesInputField
            isInvalid={isFieldInvalid("age")}
            errorMsg={getValidationError("age")}
            label="Age"
            value={inputs.age}
            /* @ts-ignore */
            onChange={({ target }) => setInputs({ age: Number(target.value.trim()) || "" })}
            placeholder="Age"
            type="number"
          />
        </UiGridItem>
        <UiGridItem colSpan={{ base: 1, md: 3 }}>
          <UiFormControl isRequired isInvalid={isFieldInvalid("ethnicity")}>
            <UiFormLabel {...textVr("body2")} color="neutral.gray800">
              Ethnicity
            </UiFormLabel>
            <EthnicityDropdown
              placeholder="Please choose"
              defaultValue={inputs.ethnicity}
              onSelect={opt => setInputs({ ethnicity: opt.value })}
            />
            {getValidationError("ethnicity") && (
              <UiFormErrorMessage mt={1} fontSize="12px" color="red">
                {getValidationError("ethnicity")}
              </UiFormErrorMessage>
            )}
          </UiFormControl>
        </UiGridItem>
        <UiGridItem colSpan={{ base: 1, md: 2 }}>
          <UiFormControl isRequired isInvalid={isFieldInvalid("eye_color")}>
            <UiFormLabel {...textVr("body2")} color="neutral.gray800">
              Eye Color
            </UiFormLabel>
            <EyeColorDropdown
              placeholder="Please choose"
              defaultValue={inputs.eye_color}
              onSelect={opt => setInputs({ eye_color: opt.value })}
            />
            {getValidationError("eye_color") && (
              <UiFormErrorMessage mt={1} fontSize="12px" color="red">
                {getValidationError("eye_color")}
              </UiFormErrorMessage>
            )}
          </UiFormControl>
        </UiGridItem>
        <UiGridItem colSpan={{ base: 1, md: 6 }}>
          <UiFormControl isRequired isInvalid={isFieldInvalid("sex")}>
            <UiFormLabel {...textVr("body2")} color="neutral.gray800">
              Sex
            </UiFormLabel>
            <UiRadioGroup mt={4} size="lg" value={inputs.sex}>
              <UiHStack spacing={3}>
                {GENDER.map(gender => (
                  <UiRadio
                    key={gender.value}
                    value={gender.value}
                    borderColor="neutral.gray200"
                    _checked={{
                      borderColor: "blue",
                      color: "blue",
                      "&::before": {
                        content: '""',
                        display: "inline-block",
                        position: "relative",
                        width: "50%",
                        height: "50%",
                        borderRadius: "50%",
                        background: "currentColor",
                      },
                    }}
                    onChange={e => onSelectGender(e.target.value)}
                  >
                    <UiText vr="body2">{gender.title}</UiText>
                  </UiRadio>
                ))}
              </UiHStack>
            </UiRadioGroup>
            {getValidationError("sex") && (
              <UiFormErrorMessage mt={1} fontSize="12px" color="red">
                {getValidationError("sex")}
              </UiFormErrorMessage>
            )}
          </UiFormControl>
        </UiGridItem>
        <UiGridItem colSpan={{ base: 1, md: 3 }}>
          <UiFormControl isRequired isInvalid={isFieldInvalid("hair_color")}>
            <UiFormLabel {...textVr("body2")} color="neutral.gray800">
              Hair Color
            </UiFormLabel>
            <HairColorDropdown
              placeholder="Please choose"
              defaultValue={inputs.hair_color}
              onSelect={opt => setInputs({ hair_color: opt.value })}
            />
            {getValidationError("hair_color") && (
              <UiFormErrorMessage mt={1} fontSize="12px" color="red">
                {getValidationError("hair_color")}
              </UiFormErrorMessage>
            )}
          </UiFormControl>
        </UiGridItem>
        <UiGridItem colSpan={{ base: 1, md: 3 }}>
          <UiFormControl isRequired isInvalid={isFieldInvalid("hair_length")}>
            <UiFormLabel {...textVr("body2")} color="neutral.gray800">
              Hair Length
            </UiFormLabel>
            <HairLengthDropdown
              placeholder="Please choose"
              defaultValue={inputs.hair_length}
              onSelect={opt => setInputs({ hair_length: opt.value })}
            />
            {getValidationError("hair_length") && (
              <UiFormErrorMessage mt={1} fontSize="12px" color="red">
                {getValidationError("hair_length")}
              </UiFormErrorMessage>
            )}
          </UiFormControl>
        </UiGridItem>

        {/* NEW: relationship field only for person 2 in two-person flow */}
        {showRelationshipField && (
          <UiGridItem colSpan={{ base: 1, md: 6 }}>
            <KeyFeaturesInputField
              isInvalid={isFieldInvalid("relationship")}
              errorMsg={getValidationError("relationship")}
              label="Relationship"
              placeholder="Ex: couple, siblings, parent & child, friends"
              value={inputs.relationship}
              onChange={({ target }) => setInputs({ relationship: target.value })}
            />
          </UiGridItem>
        )}
      </UiGrid>
    );
  }
);

export function KeyFeaturesPage() {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { screenIndex } = useSelector((state: RootState) => state.dashboard.dashboardProgress);
  const jobPendingData = useSelector((state: RootState) => state.dashboard.jobPendingData);
  const confirmKeyFeature = useSelector((state: RootState) => state.dashboard.confirmKeyFeature);
  const confirmKeyFeature2 = useSelector((state: RootState) => state.dashboard.confirmKeyFeature2);
  const photoshootType: PhotoshootType = useSelector((state: RootState) => state.dashboard.selectedType);
  // NEW: refs to access PersonForm validation methods
  const person1FormRef = React.useRef<PersonFormRef>(null);
  const person2FormRef = React.useRef<PersonFormRef>(null);

  // NEW: current step in two-person flow (1 = show person 1, 2 = show person 2)
  const [currentStep, setCurrentStep] = React.useState<1 | 2>(1);

  const vrTitle = useBreakpointValue<any>({ base: "headline4", md: "headline2" });
  const vrCaption = useBreakpointValue<any>({ base: "body2", md: "body1" });

  useEffect(() => {
    dispatch(setActiveStep({ step: 2 }));
  }, [dispatch]);

  const onClickBack = async () => {
    // NEW: back handler for person 1 vs 2
    if (photoshootType === "two_person" && currentStep === 2) {
      // When currently filling Person 2: back returns to Person 1
      setCurrentStep(1);
      return;
    }
    // When currently filling Person 1: back behaves exactly like existing logic
    history.push(PAGES.SELECT_STYLES);
  };

  const onClickNext = async () => {
    // NEW: trigger validation only when Next button is clicked
    const currentFormRef = currentStep === 1 ? person1FormRef.current : person2FormRef.current;

    if (!currentFormRef) {
      return;
    }

    // NEW: validate current form and get data
    const hasErrors = currentFormRef.validate();

    if (hasErrors) {
      return;
    }

    const currentData = currentFormRef.getData();

    // NEW: save data for current person
    if (currentStep === 1) {
      dispatch(setConfirmKeyFeature(currentData));

      // NEW: if two-person flow, move to person 2
      if (photoshootType === "two_person") {
        setCurrentStep(2);
        return;
      }
    } else {
      // Save person 2 data
      dispatch(setConfirmKeyFeature2(currentData));
    }

    // Move to next major flow step (either after person 1 in single-person flow, or after person 2 in two-person flow)
    if (jobPendingData?.upload_images?.length) {
      dispatch(setDashboardProgress({ step: EDashboardStep.Upload, screenIndex: 4 }));
    } else {
      dispatch(setDashboardProgress({ step: EDashboardStep.Upload, screenIndex: screenIndex + 1 }));
    }
    history.push(PAGES.UPLOAD_TIPS);
  };

  // NEW: dynamic title based on current step
  const getTitle = () => {
    if (photoshootType === "two_person") {
      return `Define key features for Person ${currentStep}`;
    }
    return "Define key features";
  };

  return (
    <PageLayout
      metaTagsConfig={{
        title: `Bettershot - Key features`,
        description: `Get 120 professional photoshoots in 3 hours using AI. Elevate your personal brand with corporate photoshoots.`,
        charset: "utf8",
        lang: "en",
        metas: [
          {
            name: "keywords",
            content: "corporate photoshoot,professional photoshoot,bettershot ai,linkedin profile photo,ai photoshoot",
          },
          { name: "robots", content: "noindex,nofollow" },
        ],
        links: [{ rel: "canonical", href: window.location.href }],
      }}
      as={Col}
      flex={1}
      isInGeneratingStep
    >
      <Col flex={1} justify="center">
        <UiCenter p={4} gap={5} flexDirection={{ base: "column-reverse", lg: "row" }}>
          <DashboardMainBox overflow="visible">
            <UiBox w="full" maxW={{ base: undefined, md: "43.75rem" }}>
              <UiBox px={8}>
                <UiText vr={vrTitle} textAlign="center">
                  {getTitle()}
                </UiText>
                <UiText vr={vrCaption} mt={{ base: 2, sm: 3 }} textAlign="center">
                  This information will help the AI model understand your features better and produce the best results.
                </UiText>
              </UiBox>

              {/* NEW: Show Person 1 form when currentStep is 1 */}
              {currentStep === 1 && (
                <PersonForm
                  ref={person1FormRef}
                  initialData={confirmKeyFeature}
                  personNo={1}
                  showRelationshipField={false}
                />
              )}

              {/* NEW: Show Person 2 form when currentStep is 2 (only for two-person flow) */}
              {photoshootType === "two_person" && currentStep === 2 && (
                <PersonForm ref={person2FormRef} initialData={confirmKeyFeature2} personNo={2} showRelationshipField />
              )}

              <Row
                gap={{ base: 2, sm: 5 }}
                flexDirection={{ base: "column", md: "row-reverse" }}
                align="center"
                justify="center"
                mt={8}
                w="full"
              >
                <UiPrimaryButton w="full" maxW={{ base: undefined, md: "16.25rem" }} onClick={onClickNext}>
                  Next
                </UiPrimaryButton>
                <UiPrimaryButton
                  w="full"
                  maxW={{ base: undefined, md: "16.25rem" }}
                  variant="outline"
                  borderColor="darkblue"
                  bg="transparent"
                  color="darkblue"
                  _hover={{
                    bg: "transparent",
                    color: "darkblue",
                  }}
                  onClick={onClickBack}
                >
                  <UiText vr="title1" color="darkblue">
                    Back
                  </UiText>
                </UiPrimaryButton>
              </Row>
            </UiBox>
          </DashboardMainBox>
          <DashboardStepper />
        </UiCenter>
      </Col>
    </PageLayout>
  );
}
